// Info: Import NPM Dependancies
import { useDispatch, useSelector } from "react-redux";
import uuid from "react-uuid";

// Info: Import Local Dependancies
import { useAxios } from "./axiosInterceptor";
import { getMessageHistoryMock } from "./mock";
import {
  getAppSettings,
  setAuthToken,
  setErrorMessage,
} from "../store/sitecoreSlice";
import { getUid } from "../services/commonUtil";
import { defineCancelApiObject } from "./axios.utils";
import { IAuthResponse } from "./auth";
import { useApiHeaders } from "./useApiHeaders";
import { setSessionToken, setSessionExpiry } from "./sessionStorage.utils";

export interface IPostMessageRequest {
  message: string;
  userId: string;
}

interface IDelta {
  content: string;
}
interface IChoice {
  delta: IDelta;
  finish_reason: string | null;
  index: number;
}
export interface IFaq {
  questions: string[];
}
export enum ROLE_TYPE {
  USER = "user",
  ASSISTANT = "assistant",
}

export interface IUserMessage {
  role: ROLE_TYPE;
  content: string;
  timeStamp: string;
  userId: string;
}

export interface IPostMessageResponse {
  choices: IChoice[];
}

const IS_MOCK_DATA = false;

const useProxyServices = () => {
  const { plainHeader, authHeader } = useApiHeaders();
  const appSettings = useSelector(getAppSettings);
  const dispatch = useDispatch();
  const {authenticationApi} = useAxios();

  const getFaq = async (): Promise<IFaq | null> => {
    try {
      const response = await authenticationApi.request({
        url: `${appSettings.applicationBaseUrl}${appSettings.faqAPI}`,
        headers: {
          ...authHeader,
        },
        method: "GET",
      });
      return response.data;
    } catch (error: any) {
      return null;
    }
  };

  const getMessageHistory = async (userId: string): Promise<IUserMessage[]> => {
    if (IS_MOCK_DATA) {
      return getMessageHistoryMock();
    }
    try {
      const payload = {
        userId: userId,
        uid: getUid(),
      };
      const response = await authenticationApi.request({
        //url: 'http://localhost:8081/application/chatbot-service/v1/esg-support/conversations/get',
        url: `${appSettings.applicationBaseUrl}${appSettings.conversationAPI}/get`,
        method: "POST",
        headers: {
          ...authHeader,
          "Content-Type": "application/json",
        },
        data: JSON.stringify(payload),
      });
      return response.data;
    } catch (error: any) {
      dispatch(setErrorMessage(error));
      return [];
    }
  };
  const deleteMessageHistory = async (userId: string): Promise<void> => {
    try {
      const payload = {
        userId: userId,
        uid: getUid(),
      };
      await authenticationApi.request({
        //url: 'http://localhost:8081/application/chatbot-service/v1/esg-support/conversations/delete',
        url: `${appSettings.applicationBaseUrl}${appSettings.conversationAPI}/delete`,
        method: "POST",
        headers: {
          ...authHeader,
          "Content-Type": "application/json",
        },
        data: JSON.stringify(payload),
      });
    } catch (error: any) {
      dispatch(setErrorMessage(error));
    }
  };
  const getUnAuthToken = async (cancel: boolean = false) => {
    try {
      const response: IAuthResponse = await authenticationApi.request({
        url: `${appSettings.applicationBaseUrl}${appSettings.tokenAPI}`,
        //url:"https://application-portal-proxy-service.clouduat.emiratesnbd.com/chatbot/application/authservice/v1/token/unauth",
        method: "POST",
        headers: {
          ...plainHeader,
          "accept-language": "en",
        },
        signal: cancel
          ? cancelApiObject[getUnAuthToken.name].handleRequestCancellation()
              .signal
          : undefined,
      });
      if (response?.data?.access_token) {
        dispatch(
          setAuthToken(
            `${response.data.token_type} ${response.data.access_token}`
          )
        );
        setSessionToken(response.data.access_token);
        setSessionExpiry(`${response.data.expires_in}`);
        return response;
      }
    } catch (error: any) {
      dispatch(setErrorMessage(error));
    }
  };

  const refreshToken = async () => {

    try {
      const response = await authenticationApi.request({
        url: `${appSettings.applicationBaseUrl}${appSettings.tokenAPI}`,
        method: "POST",
        headers: {
          ...plainHeader,
          "accept-language": "en",
        },
      });
  
      if (response?.data?.access_token) {
        const newToken = response?.data?.access_token;
        // dispatch(
        //   setAuthToken(
        //     `${response.data.token_type} ${response.data.access_token}`
        //   )
        // );
        setSessionToken(newToken as string);
        return newToken;
      }
    } catch (error) {
      console.error("Refresh Token API failed", error);
      return null;
    }
  };

  const cancelApiObject = defineCancelApiObject(getUnAuthToken);

  return {
    postMessage,
    getMessageHistory,
    deleteMessageHistory,
    getFaq,
    getUnAuthToken,
    refreshToken
  };
};

export { useProxyServices };
